/**
 * Record a handover protocol on the tenant's behalf (admin / super_admin).
 *
 * The tenant is normally the only party who can produce one, so a tenant who
 * never does it leaves the journey stuck and — the expensive part — the deposit
 * refund bundle permanently blocked on a missing PDF. This is the way in: the
 * admin attaches whatever paperwork they hold (a scanned paper protocol, phone
 * photos), states what happened, and saves it.
 *
 * Everything the admin approves here ends up in one PDF whose first page is an
 * attestation naming them and stating there was no tenant walkthrough. That
 * page, not a database flag, is what reaches the lessor's representative — so
 * the dialog is explicit that this is an operator entry, and says so before the
 * confirm, not after.
 *
 * The same dialog also REPLACES the document on a protocol an operator already
 * recorded (`replacing`): same fields, pre-filled from the current record, at
 * least one document required, and the tenant copy on by default. Saving always
 * goes through a confirm step — a recording is filed under the admin's name and
 * a replacement overwrites the previous PDF.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  RECORD_ACCEPT_ATTR,
  classifyRecordFiles,
  describeRecordOutcome,
} from "@/lib/protocol-admin-pdf";
import type { ProtocolKind } from "@/lib/protocol";

/** A note short enough to be meaningless is worse than none — it looks like diligence. */
const MIN_NOTE_CHARS = 12;

const label: Record<ProtocolKind, string> = {
  move_in: "move-in",
  move_out: "move-out",
};

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export type RecordProtocolSubmit = {
  files: File[];
  walkthroughDate: string;
  note: string;
  notifyTenant: boolean;
};

export function RecordProtocolDialog({
  open,
  onOpenChange,
  kind,
  tenantName,
  tenantEmail,
  busy = false,
  /** True when repairing a row that already claims submitted but has no PDF. */
  repairing = false,
  /** True when swapping the document on a protocol an operator already recorded. */
  replacing = false,
  /**
   * True when the tenant has already filed their own signed protocol and
   * this recording files a SEPARATE, additional one alongside it — never a
   * replace: only an operator's own recording can be replaced.
   */
  additionalAlongsideTenant = false,
  /**
   * Where the deposit bundle stands. It embeds the protocol PDF as it was when
   * it was assembled, so a replacement never reaches one that already exists:
   * "prepared" can still be rebuilt, "sent" (to the representative, or the
   * deposit released) cannot — the confirm says which.
   */
  bundleState = "none",
  /** The current record's note and walkthrough date, to pre-fill a replacement. */
  initialNote = null,
  initialWalkthroughDate = null,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: ProtocolKind;
  tenantName: string;
  tenantEmail: string | null;
  busy?: boolean;
  repairing?: boolean;
  replacing?: boolean;
  additionalAlongsideTenant?: boolean;
  bundleState?: "none" | "prepared" | "sent";
  initialNote?: string | null;
  /** ISO date or timestamp; only the calendar date is used. */
  initialWalkthroughDate?: string | null;
  onSubmit: (payload: RecordProtocolSubmit) => void;
}) {
  const { t } = useLanguage();
  const [files, setFiles] = useState<File[]>([]);
  const [walkthroughDate, setWalkthroughDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [notifyTenant, setNotifyTenant] = useState(false);
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // A previous attempt's files and note must not carry into a fresh one.
  //
  // A replacement starts from the record it replaces, so the admin edits rather
  // than retypes — and starts with the tenant copy ticked: unlike a first
  // recording (often months after the fact), a replacement exists to put the
  // corrected document in front of the tenant.
  useEffect(() => {
    if (!open) return;
    setFiles([]);
    setWalkthroughDate(
      (replacing && initialWalkthroughDate?.slice(0, 10)) || todayISO()
    );
    setNote(replacing ? (initialNote ?? "") : "");
    setNotifyTenant(replacing);
    setTouched(false);
    setConfirming(false);
  }, [open, replacing, initialNote, initialWalkthroughDate]);

  const classified = useMemo(() => classifyRecordFiles(files), [files]);
  const noteOk = note.trim().length >= MIN_NOTE_CHARS;
  const dateOk = !!walkthroughDate;
  // A replacement with nothing attached would swap the filed evidence for a
  // bare attestation page — that is a deletion, not a replacement.
  const usableDocs = classified.pdfs.length + classified.images.length;
  const docsOk = !replacing || usableDocs > 0;
  // Once the deposit settlement bundle has been generated (draft or sent) for
  // this booking, no protocol may be added or replaced — the bundle embeds
  // the PDF bytes as they stood at assembly, and a later protocol write would
  // leave it silently out of sync with whatever the tenant profile now shows
  // as "the" protocol. Product decision (Neel, COL-555 thread): this blocks
  // the action outright, it does not just warn before proceeding.
  const bundleBlocksSave = bundleState !== "none";
  const blocked =
    busy ||
    !noteOk ||
    !dateOk ||
    !docsOk ||
    classified.rejected.length > 0 ||
    bundleBlocksSave;

  const addFiles = (picked: FileList | null) => {
    if (!picked?.length) return;
    setFiles((prev) => [...prev, ...Array.from(picked)]);
    // Allow re-picking the same file after a removal.
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={busy ? () => {} : onOpenChange}>
      <DialogContent
        style={{
          maxWidth: 640,
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          gap: 0,
          padding: 0,
        }}
      >
        <DialogHeader style={{ padding: "22px 24px 14px" }}>
          <DialogTitle style={{ fontSize: 17, fontWeight: 700 }}>
            {replacing
              ? kind === "move_out"
                ? t(
                    "admin.protocol.record.replaceTitleMoveOut",
                    "Replace move-out protocol"
                  )
                : t(
                    "admin.protocol.record.replaceTitleMoveIn",
                    "Replace move-in protocol"
                  )
              : `Record ${label[kind]} protocol`}
          </DialogTitle>
          <DialogDescription style={{ fontSize: 13, color: "#64748B" }}>
            {replacing ? (
              `${t("admin.protocol.record.replaceDescPrefix", "Replacing the protocol filed for")} ${tenantName}${
                tenantEmail ? ` (${tenantEmail})` : ""
              }. ${t("admin.protocol.record.replaceDescSuffix", "The new documents replace the current protocol PDF and are filed under your name as an operator entry. The reference number stays the same.")}`
            ) : (
              <>
                Filed for {tenantName}
                {tenantEmail ? ` (${tenantEmail})` : ""} as an operator entry.
                It is recorded as your record of the handover, not as a
                walkthrough the tenant completed and signed.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div
          style={{
            padding: "0 24px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 14,
            overflowY: "auto",
          }}
        >
          {repairing && (
            <div
              style={{
                fontSize: 12.5,
                color: "#92400E",
                background: "#FEF6E7",
                border: "1px solid #F7E4BD",
                borderRadius: 12,
                padding: "12px 14px",
                lineHeight: 1.5,
              }}
            >
              This protocol is already marked submitted but has no document on
              file, which is what is blocking the deposit bundle. Saving it here
              attaches the document to that same record rather than creating a
              second one.
            </div>
          )}

          {additionalAlongsideTenant && (
            <div
              style={{
                fontSize: 12.5,
                color: "#1E3A8A",
                background: "#EFF6FF",
                border: "1px solid #BFDBFE",
                borderRadius: 12,
                padding: "12px 14px",
                lineHeight: 1.5,
              }}
            >
              {t(
                "admin.protocol.record.additionalAlongsideTenantBanner",
                "The tenant already filed their own signed protocol. It stays as filed — this saves a separate, additional protocol as an operator entry alongside it."
              )}
            </div>
          )}

          {replacing && !bundleBlocksSave && (
            <div
              style={{
                fontSize: 12.5,
                color: "#92400E",
                background: "#FEF6E7",
                border: "1px solid #F7E4BD",
                borderRadius: 12,
                padding: "12px 14px",
                lineHeight: 1.5,
              }}
            >
              {t(
                "admin.protocol.record.replaceBanner",
                "The current protocol document is overwritten when you save and cannot be recovered. Attach everything the new version should contain."
              )}
            </div>
          )}

          {bundleBlocksSave && (
            <div
              style={{
                fontSize: 12.5,
                color: "#991B1B",
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                borderRadius: 12,
                padding: "12px 14px",
                lineHeight: 1.5,
              }}
            >
              {bundleState === "sent"
                ? t(
                    "admin.protocol.record.bundleBlocksSentBody",
                    "The move-out bundle for this booking was already sent, or the deposit was released. Protocols can no longer be added or replaced."
                  )
                : t(
                    "admin.protocol.record.bundleBlocksPreparedBody",
                    "A move-out bundle has already been generated for this booking. Protocols can no longer be added or replaced."
                  )}
            </div>
          )}

          {/* Documents */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}>
              Documents
            </span>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={RECORD_ACCEPT_ATTR}
              onChange={(e) => addFiles(e.currentTarget.files)}
              style={{ display: "none" }}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              style={{
                border: "1px dashed #CBD5E1",
                background: "#F8FAFB",
                color: "#0F4C4A",
                borderRadius: 12,
                padding: "14px 16px",
                fontSize: 13.5,
                fontWeight: 700,
                cursor: busy ? "wait" : "pointer",
              }}
            >
              Attach PDF, JPG or PNG…
            </button>

            {files.map((f, i) => {
              const bad = classified.rejected.find((r) => r.file === f);
              return (
                <div
                  key={`${f.name}-${i}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    border: `1px solid ${bad ? "#FCA5A5" : "#ECEEF1"}`,
                    background: bad ? "#FEF2F2" : "#fff",
                    borderRadius: 10,
                    padding: "8px 12px",
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      fontSize: 13,
                      fontWeight: 600,
                      color: bad ? "#B91C1C" : "#0F172A",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {f.name}
                    {bad ? ` — ${bad.reason}` : ""}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setFiles((prev) => prev.filter((_, j) => j !== i))
                    }
                    disabled={busy}
                    style={{
                      border: "none",
                      background: "none",
                      color: "#94A3B8",
                      fontSize: 12.5,
                      fontWeight: 700,
                      cursor: busy ? "wait" : "pointer",
                    }}
                  >
                    Remove
                  </button>
                </div>
              );
            })}

            {/* In replace mode with nothing attached, "the protocol will record
                your note only" would sit beside the instruction below that a
                document is required. Say only the second. */}
            {!(replacing && usableDocs === 0) && (
              <span style={{ fontSize: 12.5, color: "#64748B" }}>
                {describeRecordOutcome(classified)}
              </span>
            )}
            {/* Not gated on `touched`: a replacement opens with a valid, pre-filled
                note, so the button is disabled from the start and can never be
                clicked to reveal why. */}
            {!docsOk && (
              <span style={{ fontSize: 12, fontWeight: 600, color: "#92400E" }}>
                {t(
                  "admin.protocol.record.replaceDocsRequired",
                  "Attach at least one document to replace the current one."
                )}
              </span>
            )}
          </div>

          {/* Walkthrough date */}
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}>
              Walkthrough date
            </span>
            <input
              type="date"
              value={walkthroughDate}
              onChange={(e) => setWalkthroughDate(e.target.value)}
              disabled={busy}
              style={{
                border: "1px solid #E2E7EC",
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 14,
                fontFamily: "inherit",
              }}
            />
            <span style={{ fontSize: 12, color: "#94A3B8" }}>
              When the handover actually happened. Today's date is recorded
              separately as the date you filed this.
            </span>
          </label>

          {/* Note */}
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}>
              What you are recording, and why the tenant did not submit it
            </span>
            <textarea
              value={note}
              onChange={(e) => {
                setTouched(true);
                setNote(e.target.value);
              }}
              rows={4}
              placeholder="e.g. Tenant moved out on 1 Oct and returned the signed paper protocol by post; scan attached. No app submission."
              disabled={busy}
              style={{
                border: `1px solid ${touched && !noteOk ? "#FCA5A5" : "#E2E7EC"}`,
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 14,
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
            {touched && !noteOk && (
              <span style={{ fontSize: 12, fontWeight: 600, color: "#B91C1C" }}>
                Write a short note — it is printed in the protocol and is the
                only explanation the deposit reviewer will see.
              </span>
            )}
          </label>

          {/* Email */}
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              background: "#F8FAFB",
              border: "1px solid #ECEEF1",
              borderRadius: 12,
              padding: "12px 14px",
            }}
          >
            <input
              type="checkbox"
              checked={notifyTenant}
              onChange={(e) => setNotifyTenant(e.target.checked)}
              disabled={busy}
              style={{ marginTop: 2 }}
            />
            <span style={{ fontSize: 13, color: "#334155", lineHeight: 1.5 }}>
              {replacing ? (
                <>
                  <strong>
                    {t(
                      "admin.protocol.record.notifyReplaceLabel",
                      "Email the tenant the new copy."
                    )}
                  </strong>{" "}
                  {t(
                    "admin.protocol.record.notifyReplaceHint",
                    "On by default so the tenant receives the new document. Untick to skip."
                  )}
                </>
              ) : (
                <>
                  <strong>Email the tenant a copy.</strong> Off by default — a
                  tenant who moved out months ago does not usually want a fresh
                  "your protocol was submitted" email.
                </>
              )}
            </span>
          </label>
        </div>

        <div
          style={{
            borderTop: "1px solid #ECEEF1",
            padding: "14px 24px",
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            style={{
              border: "1px solid #E7E9EC",
              background: "#fff",
              color: "#475569",
              borderRadius: 11,
              padding: "10px 16px",
              fontSize: 13.5,
              fontWeight: 700,
              cursor: busy ? "wait" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setTouched(true);
              if (blocked) return;
              // Nothing is written until the admin confirms below.
              setConfirming(true);
            }}
            disabled={blocked}
            style={{
              border: "1px solid #0F5C52",
              background: blocked ? "#F6F7F9" : "#0F5C52",
              color: blocked ? "#94A3B8" : "#fff",
              borderRadius: 11,
              padding: "10px 18px",
              fontSize: 13.5,
              fontWeight: 700,
              cursor: busy ? "wait" : "pointer",
            }}
          >
            {replacing
              ? busy
                ? t("admin.protocol.record.replaceSaving", "Replacing…")
                : t("admin.protocol.record.replaceSave", "Replace protocol")
              : busy
                ? "Saving…"
                : "Save protocol"}
          </button>
        </div>

        {/* The guard before anything is written: a recording is filed under the
            admin's name with no tenant signature, and a replacement overwrites
            the PDF that is on file. */}
        <AlertDialog open={confirming} onOpenChange={setConfirming}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {replacing
                  ? t(
                      "admin.protocol.record.confirmReplaceTitle",
                      "Replace the current protocol?"
                    )
                  : t(
                      "admin.protocol.record.confirmRecordTitle",
                      "File this protocol as an operator entry?"
                    )}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {`${
                  replacing
                    ? t(
                        "admin.protocol.record.confirmReplaceBody",
                        "The current document is overwritten and cannot be recovered. The replacement is filed under your name as an operator entry."
                      )
                    : t(
                        "admin.protocol.record.confirmRecordBody",
                        "It is filed under your name with no tenant walkthrough or signature. The first page of the PDF states this, and the document goes into the tenant's documents and the owner portal."
                      )
                } ${
                  notifyTenant
                    ? t(
                        "admin.protocol.record.confirmEmailTenant",
                        "The tenant will be emailed a copy."
                      )
                    : t(
                        "admin.protocol.record.confirmEmailNoTenant",
                        "The tenant will not be emailed."
                      )
                }`}
                {/* bundleBlocksSave disables Save/Replace outright (see the banner
                    above this dialog's trigger button), so this confirm step is
                    never reached while a bundle exists for the booking. */}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy}>
                {t("admin.tenantProfile.cancel", "Cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                disabled={busy}
                onClick={() =>
                  onSubmit({
                    files,
                    walkthroughDate,
                    note: note.trim(),
                    notifyTenant,
                  })
                }
              >
                {replacing
                  ? t("admin.protocol.record.replaceSave", "Replace protocol")
                  : t(
                      "admin.protocol.record.confirmRecordAction",
                      "File protocol"
                    )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
